import { yupResolver } from "@hookform/resolvers/yup"
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form"
import * as yup from "yup"
import {
    getIikoCatalogOrganizations,
    getIikoCatalogTerminalGroups,
    IIikoCatalogOrganizationsResponse,
    IIikoCatalogTerminalGroupsResponse,
    IRestaurantMenu,
    IRestaurantMenuDirectoryItem,
} from "@/shared/api/restaurant-menus"
import { Modal } from "@/shared/ui/modal"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import { RestaurantMenuFormValues, RestaurantMenuModalProps } from "../model/types"

type WizardStep = 1 | 2 | 3 | 4 | 5
type LoadingCatalog = "organizations" | "terminal-groups" | null

const emptyValues: RestaurantMenuFormValues = {
    name: "",
    api_login: "",
    organization_id: "",
    external_menu_id: "",
    terminal_group_id: "",
    default_order_type_id: "",
}

const stepTitles = [
    "API login",
    "Организация",
    "Внешнее меню IIKO",
    "Терминальная группа и тип заказа",
    "Название и подтверждение",
]

const schema: yup.ObjectSchema<RestaurantMenuFormValues> = yup.object({
    name: yup.string().trim().required("Введите название меню"),
    api_login: yup.string().trim().required("Введите API login"),
    organization_id: yup.string().trim().required("Укажите организацию"),
    external_menu_id: yup.string().trim().defined(),
    terminal_group_id: yup.string().trim().required("Укажите терминальную группу"),
    default_order_type_id: yup.string().trim().required("Укажите тип заказа"),
})

const getInitialValues = (menu: IRestaurantMenu | null): RestaurantMenuFormValues => {
    if (!menu) {
        return emptyValues
    }

    return {
        name: menu.name ?? "",
        api_login: menu.api_login ?? "",
        organization_id: menu.organization_id ?? "",
        external_menu_id: menu.external_menu_id ?? "",
        terminal_group_id: menu.iiko_config?.terminal_group_id ?? "",
        default_order_type_id: menu.iiko_config?.default_order_type_id ?? "",
    }
}

const getDirectoryLabel = (item: IRestaurantMenuDirectoryItem): string => {
    return item.name || item.id || "Без названия"
}

const hasDirectoryItem = (items: IRestaurantMenuDirectoryItem[], id: string): boolean => {
    return items.some((item) => item.id === id)
}

const terminalCatalogKey = (apiLogin: string, organizationId: string): string => {
    return `${apiLogin}\u0000${organizationId}`
}

const inputClassName =
    "w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark bg-transparent"

export const RestaurantMenuModal = ({
    isOpen,
    currentMenu,
    isSubmitting,
    serverError,
    onClose,
    onSubmit,
}: RestaurantMenuModalProps) => {
    const isEditing = Boolean(currentMenu)
    const initialValues = useMemo(() => getInitialValues(currentMenu), [currentMenu])
    const organizationCache = useRef(new Map<string, IIikoCatalogOrganizationsResponse>())
    const terminalCache = useRef(new Map<string, IIikoCatalogTerminalGroupsResponse>())
    const catalogRequestId = useRef(0)
    const [step, setStep] = useState<WizardStep>(1)
    const [organizationCatalog, setOrganizationCatalog] = useState<IIikoCatalogOrganizationsResponse | null>(null)
    const [terminalCatalog, setTerminalCatalog] = useState<IIikoCatalogTerminalGroupsResponse | null>(null)
    const [loadingCatalog, setLoadingCatalog] = useState<LoadingCatalog>(null)
    const [catalogError, setCatalogError] = useState<string | null>(null)
    const [manualOrganizations, setManualOrganizations] = useState(false)
    const [manualTerminals, setManualTerminals] = useState(false)
    const [externalMenuSelected, setExternalMenuSelected] = useState(false)
    const [externalMenuError, setExternalMenuError] = useState(false)

    const {
        control,
        register,
        handleSubmit,
        reset,
        trigger,
        watch,
        getValues,
        setValue,
        setError,
        clearErrors,
        setFocus,
        formState: { errors, isDirty },
    } = useForm<RestaurantMenuFormValues>({
        defaultValues: emptyValues,
        resolver: yupResolver(schema) as Resolver<RestaurantMenuFormValues>,
    })

    const apiLogin = watch("api_login")
    const organizationId = watch("organization_id")
    const externalMenuId = watch("external_menu_id")
    const terminalGroupId = watch("terminal_group_id")
    const orderTypeId = watch("default_order_type_id")
    const name = watch("name")

    useEffect(() => {
        if (!isOpen) {
            reset(emptyValues)
            return
        }

        reset(initialValues)
        catalogRequestId.current += 1
        organizationCache.current.clear()
        terminalCache.current.clear()
        setStep(1)
        setOrganizationCatalog(null)
        setTerminalCatalog(null)
        setLoadingCatalog(null)
        setCatalogError(null)
        setManualOrganizations(false)
        setManualTerminals(false)
        setExternalMenuSelected(isEditing)
        setExternalMenuError(false)
    }, [initialValues, isEditing, isOpen, reset])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        if (step === 4) {
            return
        }

        const fieldByStep: Record<Exclude<WizardStep, 4>, keyof RestaurantMenuFormValues> = {
            1: "api_login",
            2: "organization_id",
            3: "external_menu_id",
            5: "name",
        }
        setFocus(fieldByStep[step])
    }, [isOpen, setFocus, step])

    useEffect(() => {
        if (
            !isOpen ||
            step !== 4 ||
            loadingCatalog === "terminal-groups" ||
            catalogError
        ) {
            return
        }

        setFocus("terminal_group_id")
    }, [
        catalogError,
        isOpen,
        loadingCatalog,
        manualTerminals,
        setFocus,
        step,
        terminalCatalog,
    ])

    const invalidateAfterApiLogin = useCallback(() => {
        catalogRequestId.current += 1
        setOrganizationCatalog(null)
        setTerminalCatalog(null)
        setLoadingCatalog(null)
        setManualOrganizations(false)
        setManualTerminals(false)
        setCatalogError(null)
        setExternalMenuSelected(false)
        setExternalMenuError(false)
        setValue("organization_id", "", { shouldDirty: true })
        setValue("external_menu_id", "", { shouldDirty: true })
        setValue("terminal_group_id", "", { shouldDirty: true })
        setValue("default_order_type_id", "", { shouldDirty: true })
        clearErrors(["organization_id", "terminal_group_id", "default_order_type_id"])
    }, [clearErrors, setValue])

    const invalidateAfterOrganization = useCallback(() => {
        catalogRequestId.current += 1
        setTerminalCatalog(null)
        setLoadingCatalog(null)
        setManualTerminals(false)
        setCatalogError(null)
        setValue("terminal_group_id", "", { shouldDirty: true })
        setValue("default_order_type_id", "", { shouldDirty: true })
        clearErrors(["terminal_group_id", "default_order_type_id"])
    }, [clearErrors, setValue])

    const apiLoginRegistration = register("api_login")
    const organizationRegistration = register("organization_id")

    const handleApiLoginChange = (event: ChangeEvent<HTMLInputElement>) => {
        const previousValue = getValues("api_login")
        apiLoginRegistration.onChange(event)
        if (event.target.value !== previousValue) {
            invalidateAfterApiLogin()
        }
    }

    const handleOrganizationChange = (event: ChangeEvent<HTMLInputElement>) => {
        const previousValue = getValues("organization_id")
        organizationRegistration.onChange(event)
        if (event.target.value !== previousValue) {
            invalidateAfterOrganization()
        }
    }

    const loadOrganizations = async (moveNext = true) => {
        const isApiLoginValid = await trigger("api_login")
        if (!isApiLoginValid) {
            return
        }

        const normalizedApiLogin = getValues("api_login").trim()
        const requestId = ++catalogRequestId.current
        setValue("api_login", normalizedApiLogin, { shouldDirty: true })
        setCatalogError(null)

        const cachedCatalog = organizationCache.current.get(normalizedApiLogin)
        if (cachedCatalog) {
            setLoadingCatalog(null)
            setOrganizationCatalog(cachedCatalog)
            setManualOrganizations(false)
            if (moveNext) {
                setStep(2)
            }
            return
        }

        try {
            setLoadingCatalog("organizations")
            const response = await getIikoCatalogOrganizations(normalizedApiLogin)

            if (
                requestId !== catalogRequestId.current ||
                getValues("api_login").trim() !== normalizedApiLogin
            ) {
                return
            }

            organizationCache.current.set(normalizedApiLogin, response.data)
            setOrganizationCatalog(response.data)
            setManualOrganizations(false)
            if (moveNext) {
                setStep(2)
            }
        } catch (error) {
            if (
                requestId === catalogRequestId.current &&
                getValues("api_login").trim() === normalizedApiLogin
            ) {
                setCatalogError(getApiErrorMessage(error))
            }
        } finally {
            if (
                requestId === catalogRequestId.current &&
                getValues("api_login").trim() === normalizedApiLogin
            ) {
                setLoadingCatalog((currentLoading) => (
                    currentLoading === "organizations" ? null : currentLoading
                ))
            }
        }
    }

    const continueEditWithoutOrganizations = async () => {
        const isApiLoginValid = await trigger("api_login")
        if (!isApiLoginValid) {
            return
        }

        setManualOrganizations(true)
        setCatalogError(null)
        setStep(2)
    }

    const useManualOrganizations = () => {
        setManualOrganizations(true)
        setCatalogError(null)
        setStep(2)
    }

    const continueFromOrganization = async () => {
        const isOrganizationValid = await trigger("organization_id")
        if (!isOrganizationValid) {
            return
        }

        const selectedId = getValues("organization_id").trim()
        const isSavedEditValue =
            isEditing &&
            selectedId === initialValues.organization_id &&
            getValues("api_login").trim() === initialValues.api_login.trim()

        if (
            organizationCatalog &&
            !manualOrganizations &&
            !hasDirectoryItem(organizationCatalog.organizations, selectedId) &&
            !isSavedEditValue
        ) {
            setError("organization_id", {
                type: "manual",
                message: "Выберите организацию из справочника",
            })
            return
        }

        setValue("organization_id", selectedId, { shouldDirty: true })
        setStep(3)
    }

    const loadTerminals = async () => {
        const normalizedApiLogin = getValues("api_login").trim()
        const normalizedOrganizationId = getValues("organization_id").trim()
        const cacheKey = terminalCatalogKey(normalizedApiLogin, normalizedOrganizationId)
        const requestId = ++catalogRequestId.current
        setCatalogError(null)

        const cachedCatalog = terminalCache.current.get(cacheKey)
        if (cachedCatalog) {
            setLoadingCatalog(null)
            setTerminalCatalog(cachedCatalog)
            setManualTerminals(false)
            return
        }

        try {
            setLoadingCatalog("terminal-groups")
            const response = await getIikoCatalogTerminalGroups(normalizedApiLogin, normalizedOrganizationId)

            if (
                requestId !== catalogRequestId.current ||
                terminalCatalogKey(
                    getValues("api_login").trim(),
                    getValues("organization_id").trim()
                ) !== cacheKey
            ) {
                return
            }

            terminalCache.current.set(cacheKey, response.data)
            setTerminalCatalog(response.data)
            setManualTerminals(false)
        } catch (error) {
            if (
                requestId === catalogRequestId.current &&
                terminalCatalogKey(
                    getValues("api_login").trim(),
                    getValues("organization_id").trim()
                ) === cacheKey
            ) {
                setCatalogError(getApiErrorMessage(error))
            }
        } finally {
            if (
                requestId === catalogRequestId.current &&
                terminalCatalogKey(
                    getValues("api_login").trim(),
                    getValues("organization_id").trim()
                ) === cacheKey
            ) {
                setLoadingCatalog((currentLoading) => (
                    currentLoading === "terminal-groups" ? null : currentLoading
                ))
            }
        }
    }

    const continueFromExternalMenu = async () => {
        if (!externalMenuSelected) {
            setExternalMenuError(true)
            return
        }

        setExternalMenuError(false)
        setStep(4)

        const usesPrefilledTerminalValues =
            isEditing &&
            Boolean(initialValues.terminal_group_id) &&
            Boolean(initialValues.default_order_type_id) &&
            getValues("api_login").trim() === initialValues.api_login.trim() &&
            getValues("organization_id").trim() === initialValues.organization_id.trim()

        if (usesPrefilledTerminalValues && !terminalCatalog) {
            setManualTerminals(true)
            return
        }

        await loadTerminals()
    }

    const useManualTerminals = () => {
        setManualTerminals(true)
        setCatalogError(null)
    }

    const continueFromTerminals = async () => {
        const isTerminalSelectionValid = await trigger(["terminal_group_id", "default_order_type_id"])
        if (!isTerminalSelectionValid) {
            return
        }

        const selectedTerminalGroupId = getValues("terminal_group_id").trim()
        const selectedOrderTypeId = getValues("default_order_type_id").trim()
        const usesSavedEditValues =
            isEditing &&
            getValues("api_login").trim() === initialValues.api_login.trim() &&
            getValues("organization_id").trim() === initialValues.organization_id.trim() &&
            selectedTerminalGroupId === initialValues.terminal_group_id &&
            selectedOrderTypeId === initialValues.default_order_type_id

        if (
            terminalCatalog &&
            !manualTerminals &&
            !hasDirectoryItem(terminalCatalog.terminal_groups, selectedTerminalGroupId) &&
            !usesSavedEditValues
        ) {
            setError("terminal_group_id", {
                type: "manual",
                message: "Выберите терминальную группу из справочника",
            })
            return
        }

        if (
            terminalCatalog &&
            !manualTerminals &&
            !hasDirectoryItem(terminalCatalog.order_types, selectedOrderTypeId) &&
            !usesSavedEditValues
        ) {
            setError("default_order_type_id", {
                type: "manual",
                message: "Выберите тип заказа из справочника",
            })
            return
        }

        setValue("terminal_group_id", selectedTerminalGroupId, { shouldDirty: true })
        setValue("default_order_type_id", selectedOrderTypeId, { shouldDirty: true })
        setStep(5)
    }

    const requestClose = () => {
        if (isSubmitting) {
            return
        }

        if (isDirty && !window.confirm("Закрыть форму и удалить несохранённые изменения?")) {
            return
        }

        onClose()
    }

    const submit: SubmitHandler<RestaurantMenuFormValues> = async (values) => {
        if (step !== 5) {
            return
        }

        await onSubmit(values)
    }

    const handleWizardSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (step === 5) {
            void handleSubmit(submit)(event)
            return
        }

        if (step === 1) {
            if (loadingCatalog !== "organizations") {
                void loadOrganizations()
            }
            return
        }

        if (step === 2) {
            void continueFromOrganization()
            return
        }

        if (step === 3) {
            void continueFromExternalMenu()
            return
        }

        if (loadingCatalog === "terminal-groups") {
            return
        }

        if (catalogError) {
            void loadTerminals()
            return
        }

        void continueFromTerminals()
    }

    const renderLoading = () => (
        <div className="info_panel flex items-center gap-3" role="status" aria-live="polite">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#B9C9C6] border-t-color-dark" />
            загружаем справочник из IIKO
        </div>
    )

    const renderCatalogError = (onRetry: () => void, onManual: () => void) => (
        <div className="info_panel !bg-[#FFF4F4] flex flex-col gap-3" role="alert">
            <div className="text-color-danger">{catalogError}</div>
            <div className="flex flex-wrap gap-3">
                <button type="button" className="form_handle_button !w-auto px-6" onClick={onRetry}>
                    Повторить
                </button>
                <button type="button" className="form_cancel_button !w-auto px-6" onClick={onManual}>
                    Ввести ID вручную
                </button>
            </div>
        </div>
    )

    return (
        <Modal
            isOpen={isOpen}
            title={isEditing ? "Редактировать меню" : "Создать меню"}
            subtitle={`Шаг ${step} из 5 · ${stepTitles[step - 1]}`}
            onRequestClose={requestClose}
        >
            <form onSubmit={handleWizardSubmit} className="flex min-w-[640px] max-w-[760px] flex-col gap-5">
                <div className="grid grid-cols-5 gap-2" aria-label={`Шаг ${step} из 5`}>
                    {stepTitles.map((title, index) => {
                        const stepNumber = index + 1
                        return (
                            <div
                                key={title}
                                className={`h-1.5 rounded-full ${stepNumber <= step ? "bg-color-dark" : "bg-[#E4E9E8]"}`}
                            />
                        )
                    })}
                </div>

                {serverError && (
                    <div className="info_panel !bg-[#FFF4F4] !text-color-danger" role="alert">
                        {serverError}
                    </div>
                )}

                {step === 1 && (
                    <>
                        <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${errors.api_login ? "border-color-danger" : ""}`}>
                            <div className="flex w-full items-center justify-between gap-4">
                                <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-api-login">
                                    API login
                                </label>
                                {errors.api_login && <span className="text-base text-color-danger">{errors.api_login.message}</span>}
                            </div>
                            <input
                                id="restaurant-menu-api-login"
                                placeholder="Введите API login"
                                className={inputClassName}
                                name={apiLoginRegistration.name}
                                ref={apiLoginRegistration.ref}
                                onBlur={apiLoginRegistration.onBlur}
                                onChange={handleApiLoginChange}
                                disabled={loadingCatalog === "organizations"}
                            />
                        </div>

                        <div className="text-sm text-[#777777]">
                            Справочник загружается только после нажатия кнопки. API login не отправляется при открытии формы.
                        </div>

                        {loadingCatalog === "organizations" && renderLoading()}
                        {catalogError && loadingCatalog !== "organizations" && renderCatalogError(
                            () => void loadOrganizations(),
                            useManualOrganizations
                        )}

                        <div className="flex gap-3">
                            <button type="button" className="form_cancel_button" onClick={requestClose}>
                                Отменить
                            </button>
                            {isEditing && apiLogin.trim() === initialValues.api_login.trim() && (
                                <button
                                    type="button"
                                    className="form_cancel_button"
                                    disabled={!apiLogin.trim() || loadingCatalog === "organizations"}
                                    onClick={() => void continueEditWithoutOrganizations()}
                                >
                                    Далее без загрузки
                                </button>
                            )}
                            <button
                                type="button"
                                className="form_handle_button"
                                disabled={!apiLogin.trim() || loadingCatalog === "organizations"}
                                onClick={() => void loadOrganizations()}
                            >
                                Загрузить
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${errors.organization_id ? "border-color-danger" : ""}`}>
                            <div className="flex w-full items-center justify-between gap-4">
                                <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-organization">
                                    Организация
                                </label>
                                {errors.organization_id && <span className="text-base text-color-danger">{errors.organization_id.message}</span>}
                            </div>
                            <input
                                id="restaurant-menu-organization"
                                list={organizationCatalog && !manualOrganizations ? "restaurant-menu-organizations" : undefined}
                                placeholder={organizationCatalog && !manualOrganizations ? "Начните вводить название или ID" : "Введите organization_id"}
                                className={inputClassName}
                                name={organizationRegistration.name}
                                ref={organizationRegistration.ref}
                                onBlur={organizationRegistration.onBlur}
                                onChange={handleOrganizationChange}
                                autoComplete="off"
                            />
                            {organizationCatalog && !manualOrganizations && (
                                <datalist id="restaurant-menu-organizations">
                                    {isEditing &&
                                        Boolean(organizationId) &&
                                        organizationId === initialValues.organization_id &&
                                        !hasDirectoryItem(organizationCatalog.organizations, organizationId) && (
                                        <option value={organizationId} label={`${organizationId} (сохранённое)`} />
                                    )}
                                    {organizationCatalog.organizations.map((organization) => (
                                        <option key={organization.id} value={organization.id} label={getDirectoryLabel(organization)} />
                                    ))}
                                </datalist>
                            )}
                        </div>

                        <div className="text-sm text-[#777777]">
                            {organizationCatalog && !manualOrganizations
                                ? "Поле поддерживает поиск по названию и ID среди загруженных организаций."
                                : "Ручной режим: укажите ID организации из IIKO."}
                        </div>

                        <div className="flex gap-3">
                            <button type="button" className="form_cancel_button" onClick={() => setStep(1)}>
                                Назад
                            </button>
                            <button
                                type="button"
                                className="form_handle_button"
                                disabled={!organizationId.trim()}
                                onClick={() => void continueFromOrganization()}
                            >
                                Далее
                            </button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${externalMenuError ? "border-color-danger" : ""}`}>
                            <div className="flex w-full items-center justify-between gap-4">
                                <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-external">
                                    Внешнее меню IIKO
                                </label>
                                {externalMenuError && <span className="text-base text-color-danger">Выберите вариант</span>}
                            </div>
                            {organizationCatalog && !manualOrganizations ? (
                                <Controller
                                    name="external_menu_id"
                                    control={control}
                                    render={({ field }) => {
                                        const isSavedExternalMenuMissing = Boolean(
                                            isEditing &&
                                            field.value &&
                                            !hasDirectoryItem(organizationCatalog.external_menus, field.value)
                                        )

                                        return (
                                            <select
                                                {...field}
                                                id="restaurant-menu-external"
                                                className={inputClassName}
                                                value={externalMenuSelected ? field.value : "__not_selected__"}
                                                onChange={(event) => {
                                                    field.onChange(event.target.value)
                                                    setExternalMenuSelected(true)
                                                    setExternalMenuError(false)
                                                }}
                                            >
                                                <option value="__not_selected__" disabled>Выберите меню</option>
                                                <option value="">— поиск по имени —</option>
                                                {isSavedExternalMenuMissing && (
                                                    <option value={field.value}>{field.value} (сохранённое)</option>
                                                )}
                                                {organizationCatalog.external_menus.map((externalMenu) => (
                                                    <option key={externalMenu.id} value={externalMenu.id}>
                                                        {getDirectoryLabel(externalMenu)}
                                                    </option>
                                                ))}
                                            </select>
                                        )
                                    }}
                                />
                            ) : (
                                <Controller
                                    name="external_menu_id"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            id="restaurant-menu-external"
                                            placeholder="Введите external_menu_id"
                                            className={inputClassName}
                                            onChange={(event) => {
                                                field.onChange(event)
                                                setExternalMenuSelected(Boolean(event.target.value.trim()))
                                                setExternalMenuError(false)
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </div>

                        {(!organizationCatalog || manualOrganizations) && (
                            <button
                                type="button"
                                className={`rounded-xl border px-4 py-3 text-left ${externalMenuSelected && !externalMenuId ? "border-color-dark bg-[#F3F7F6]" : "border-[#B9C9C6]"}`}
                                onClick={() => {
                                    setValue("external_menu_id", "", { shouldDirty: true })
                                    setExternalMenuSelected(true)
                                    setExternalMenuError(false)
                                }}
                            >
                                Искать меню по имени
                            </button>
                        )}

                        <div className="text-sm text-[#777777]">
                            Если выбрать поиск по имени, название на последнем шаге должно точно совпадать с названием меню в IIKO.
                        </div>

                        <div className="flex gap-3">
                            <button type="button" className="form_cancel_button" onClick={() => setStep(2)}>
                                Назад
                            </button>
                            <button
                                type="button"
                                className="form_handle_button"
                                disabled={!externalMenuSelected}
                                onClick={() => void continueFromExternalMenu()}
                            >
                                Далее
                            </button>
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        {loadingCatalog === "terminal-groups" && renderLoading()}
                        {catalogError && loadingCatalog !== "terminal-groups" && renderCatalogError(
                            () => void loadTerminals(),
                            useManualTerminals
                        )}

                        {loadingCatalog !== "terminal-groups" && !catalogError && (
                            <>
                                <div className="grid grid-cols-2 gap-5">
                                    <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${errors.terminal_group_id ? "border-color-danger" : ""}`}>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-terminal">
                                                Терминальная группа
                                            </label>
                                            {errors.terminal_group_id && <span className="text-sm text-color-danger">{errors.terminal_group_id.message}</span>}
                                        </div>
                                        {terminalCatalog && !manualTerminals ? (
                                            <select id="restaurant-menu-terminal" className={inputClassName} {...register("terminal_group_id")}>
                                                <option value="">Выберите группу</option>
                                                {terminalCatalog.terminal_groups.map((terminalGroup) => (
                                                    <option key={terminalGroup.id} value={terminalGroup.id}>
                                                        {getDirectoryLabel(terminalGroup)}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                id="restaurant-menu-terminal"
                                                placeholder="Введите terminal_group_id"
                                                className={inputClassName}
                                                {...register("terminal_group_id")}
                                            />
                                        )}
                                    </div>

                                    <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${errors.default_order_type_id ? "border-color-danger" : ""}`}>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-order-type">
                                                Тип заказа
                                            </label>
                                            {errors.default_order_type_id && <span className="text-sm text-color-danger">{errors.default_order_type_id.message}</span>}
                                        </div>
                                        {terminalCatalog && !manualTerminals ? (
                                            <select id="restaurant-menu-order-type" className={inputClassName} {...register("default_order_type_id")}>
                                                <option value="">Выберите тип заказа</option>
                                                {terminalCatalog.order_types.map((orderType) => (
                                                    <option key={orderType.id} value={orderType.id}>
                                                        {getDirectoryLabel(orderType)}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                id="restaurant-menu-order-type"
                                                placeholder="Введите default_order_type_id"
                                                className={inputClassName}
                                                {...register("default_order_type_id")}
                                            />
                                        )}
                                    </div>
                                </div>

                                {manualTerminals && isEditing && (
                                    <button
                                        type="button"
                                        className="form_cancel_button !w-auto self-start px-6"
                                        onClick={() => void loadTerminals()}
                                    >
                                        Загрузить справочник
                                    </button>
                                )}

                                {terminalCatalog && !manualTerminals && (
                                    <button
                                        type="button"
                                        className="text-left text-sm text-[#777777] underline"
                                        onClick={useManualTerminals}
                                    >
                                        Ввести ID вручную
                                    </button>
                                )}
                            </>
                        )}

                        <div className="flex gap-3">
                            <button type="button" className="form_cancel_button" onClick={() => setStep(3)}>
                                Назад
                            </button>
                            <button
                                type="button"
                                className="form_handle_button"
                                disabled={
                                    loadingCatalog === "terminal-groups" ||
                                    Boolean(catalogError) ||
                                    !terminalGroupId.trim() ||
                                    !orderTypeId.trim()
                                }
                                onClick={() => void continueFromTerminals()}
                            >
                                Далее
                            </button>
                        </div>
                    </>
                )}

                {step === 5 && (
                    <>
                        <div className={`flex flex-col gap-[6px] border-b-[1.5px] border-[#B9C9C6] pb-[8px] ${errors.name ? "border-color-danger" : ""}`}>
                            <div className="flex w-full items-center justify-between gap-4">
                                <label className="text-base font-semibold uppercase text-[#828282]" htmlFor="restaurant-menu-name">
                                    Название
                                </label>
                                {errors.name && <span className="text-base text-color-danger">{errors.name.message}</span>}
                            </div>
                            <input
                                id="restaurant-menu-name"
                                placeholder="Введите название меню"
                                className={inputClassName}
                                {...register("name")}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4 rounded-2xl bg-[#F7F8FA] p-4 text-sm">
                            <div>
                                <div className="text-[#828282]">Организация</div>
                                <div className="break-all">{organizationId}</div>
                            </div>
                            <div>
                                <div className="text-[#828282]">Внешнее меню</div>
                                <div className="break-all">{externalMenuId || "поиск по имени"}</div>
                            </div>
                            <div>
                                <div className="text-[#828282]">Терминальная группа</div>
                                <div className="break-all">{terminalGroupId}</div>
                            </div>
                            <div>
                                <div className="text-[#828282]">Тип заказа</div>
                                <div className="break-all">{orderTypeId}</div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1 rounded-2xl border border-[#B9C9C6] p-4">
                            <span className="text-sm font-semibold uppercase text-[#828282]">Slug</span>
                            <span className="break-all text-lg">{isEditing ? currentMenu?.slug || "—" : "Будет создан сервером"}</span>
                            <span className="text-sm text-[#777777]">Slug доступен только для чтения.</span>
                        </div>

                        <div className="flex gap-3">
                            <button type="button" className="form_cancel_button" onClick={() => setStep(4)}>
                                Назад
                            </button>
                            <button
                                type="submit"
                                className="form_handle_button"
                                disabled={isSubmitting || !name.trim()}
                            >
                                {isSubmitting ? "Сохраняем..." : isEditing ? "Сохранить" : "Создать"}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </Modal>
    )
}
